import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Search, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { API, fixEncoding, SEO_CONFIG } from "@/utils/helpers";
import { 
  generateBrandSchema, 
  generateBreadcrumbSchema, 
  generateBrandOffersSchema,
  combineSchemas,
  useStructuredData
} from "@/utils/structuredData";

const BrandPage = () => {
  const { brandName } = useParams();
  const navigate = useNavigate();
  const [brandData, setBrandData] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeLetter, setActiveLetter] = useState('');
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  useEffect(() => {
    const fetchBrandData = async () => {
      if (!brandName) return;
      
      setLoading(true);
      setError('');
      
      try {
        const [brandRes, offersRes] = await Promise.all([
          axios.get(`${API}/brand/${encodeURIComponent(brandName)}`),
          axios.get(`${API}/brand/${encodeURIComponent(brandName)}/offers`)
        ]);
        
        setBrandData(brandRes.data);
        setOffers(offersRes.data.offers || []);
      } catch (err) {
        console.error('Error fetching brand data:', err);
        if (err.response?.status === 404) {
          setError(`La marque "${brandName}" n'a pas été trouvée.`);
        } else {
          setError('Erreur lors du chargement des données de la marque.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchBrandData();
  }, [brandName]);

  const handleOfferClick = (offer) => {
    window.open(offer.goUrl, '_blank', 'noopener,noreferrer');
  };

  const scrollToLetter = (letter) => {
    const element = document.getElementById(`query-letter-${letter}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveLetter(letter);
    }
  };

  // SEO structured data for brand page - comprehensive schemas
  const breadcrumbSchema = brandData ? generateBreadcrumbSchema([
    { name: 'Accueil', url: SEO_CONFIG.siteUrl },
    { name: 'Marques', url: `${SEO_CONFIG.siteUrl}/#brands` },
    { name: brandData.brand, url: `${SEO_CONFIG.siteUrl}/brand/${encodeURIComponent(brandData.brand)}` }
  ]) : null;

  const brandSchema = brandData ? generateBrandSchema(brandData) : null;
  const offersSchema = brandData && offers.length > 0 ? generateBrandOffersSchema(brandData, offers) : null;
  
  const structuredData = combineSchemas(breadcrumbSchema, brandSchema, offersSchema);
  
  // Inject structured data into document head
  useStructuredData(structuredData, 'brand-structured-data');

  if (loading) {
    return (
      <div className="brand-page">
        <Helmet>
          <title>{`${brandName} | ${SEO_CONFIG.siteName}`}</title>
        </Helmet>
        <div className="brand-page-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Chargement de la marque...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="brand-page">
        <Helmet>
          <title>{`${brandName} - Erreur | ${SEO_CONFIG.siteName}`}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="brand-page-container">
          <div className="error-state">
            <p>{error}</p>
            <Button onClick={() => navigate('/')} className="back-home-btn">
              Retour à l'accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const queryLetters = brandData?.queries_by_letter ? Object.keys(brandData.queries_by_letter).sort() : [];

  return (
    <div className="brand-page" data-testid="brand-page">
      <Helmet>
        <title>{`${brandData?.brand} - Meilleures offres et promotions | ${SEO_CONFIG.siteName}`}</title>
        <meta name="description" content={`Découvrez les meilleures offres ${brandData?.brand}. ${brandData?.total_results?.toLocaleString()} produits disponibles avec les meilleurs prix.`} />
        <meta name="keywords" content={`${brandData?.brand}, offres ${brandData?.brand}, promotions ${brandData?.brand}, meilleur prix ${brandData?.brand}`} />
        <meta property="og:title" content={`${brandData?.brand} - Meilleures offres | ${SEO_CONFIG.siteName}`} />
        <meta property="og:description" content={`Découvrez les meilleures offres ${brandData?.brand}. ${brandData?.total_results?.toLocaleString()} produits disponibles.`} />
        <meta property="og:type" content="website" />
        {brandData?.logo_url && <meta property="og:image" content={brandData.logo_url} />}
        <link rel="canonical" href={`${SEO_CONFIG.siteUrl}/brand/${encodeURIComponent(brandData?.brand)}`} />
      </Helmet>

      <header className="brand-page-header">
        <div className="brand-page-header-content">
          <a href="/" className="brand-page-logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_shop-navigator-13/artifacts/hmk599ai_marques-and-co_logo.jpg" 
              alt="Marques & Co" 
              className="brand-page-logo"
            />
          </a>
        </div>
      </header>

      <div className="brand-page-container">
        <section className="brand-hero" data-testid="brand-hero">
          <h1 className="brand-hero-title">{brandData?.brand}</h1>
          
          {(brandData?.logo_url || brandData?.summary) && (
            <div className="brand-hero-content">
              {brandData?.logo_url && (
                <img 
                  src={brandData.logo_url} 
                  alt={brandData?.brand} 
                  className="brand-hero-logo"
                  data-testid="brand-logo"
                />
              )}
              {brandData?.summary && (
                <div className="brand-hero-summary-wrapper">
                  <div 
                    className={`brand-hero-summary ${summaryExpanded ? 'expanded' : ''}`}
                    dangerouslySetInnerHTML={{ __html: brandData.summary }}
                    data-testid="brand-summary"
                  />
                  <button 
                    className="brand-summary-toggle"
                    onClick={() => setSummaryExpanded(!summaryExpanded)}
                    data-testid="summary-toggle"
                  >
                    {summaryExpanded ? 'Voir moins' : 'Voir plus'}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        {brandData?.top_queries?.length > 0 && (
          <section className="brand-queries-section" data-testid="brand-top-queries">
            <h2 className="section-title-brand">Recherches populaires {brandData?.brand}</h2>
            <div className="brand-queries-grid">
              {brandData.top_queries.slice(0, 30).map((query) => (
                <a
                  key={query.full_query}
                  href={`/?query=${encodeURIComponent(query.full_query)}&page=1`}
                  className="brand-query-chip"
                  data-testid={`query-chip-${query.full_query.replace(/\s+/g, '-')}`}
                >
                  <Search className="brand-query-icon" />
                  <span>{query.full_query}</span>
                  <span className="brand-query-count">({query.result_count})</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {offers.length > 0 && (
          <section className="brand-offers-section" data-testid="brand-offers">
            <h2 className="section-title-brand">Top offres en promotion</h2>
            <div className="products-grid">
              {offers.map((offer) => (
                <Card 
                  key={offer.offerId} 
                  className="product-card"
                  data-testid="product-card"
                  onClick={() => handleOfferClick(offer)}
                >
                  <div className="product-image-container">
                    {offer.images?.[0]?.url && (
                      <img
                        src={offer.images[0].url}
                        alt={offer.title}
                        className="product-image"
                        loading="lazy"
                      />
                    )}
                    <div className="price-overlay">
                      <span className="price-value">{offer.price?.toFixed(2)}€</span>
                    </div>
                    {offer.rebatePercentage > 0 && (
                      <Badge className="rebate-badge">-{offer.rebatePercentage}%</Badge>
                    )}
                  </div>
                  
                  <div className="product-content">
                    <div className="merchant-row">
                      <span className="merchant-name">{fixEncoding(offer.merchant?.name)}</span>
                      {offer.deliveryCost === 0 && (
                        <span className="delivery-icon" title="Livraison gratuite">🚚</span>
                      )}
                    </div>
                    
                    <h3 className="product-title" title={fixEncoding(offer.title)}>{fixEncoding(offer.title)}</h3>
                    
                    <div className="product-actions">
                      <Button
                        data-testid="view-offer-button"
                        onClick={(e) => { e.stopPropagation(); handleOfferClick(offer); }}
                        className="offer-button"
                      >
                        <span>Voir l'offre</span>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      
                      {offer.code?.ean && (
                        <Button
                          data-testid="compare-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/compare/${offer.code.ean}?query=${encodeURIComponent(brandData?.brand)}`);
                          }}
                          className="compare-button"
                          variant="outline"
                        >
                          <span>Comparer</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {queryLetters.length > 0 && (
          <section className="brand-query-index" data-testid="brand-query-index">
            <h2 className="section-title-brand">Toutes les recherches {brandData?.brand} A-Z</h2>
            
            <nav className="brand-query-alphabet-nav">
              {queryLetters.map(letter => (
                <button
                  key={letter}
                  onClick={() => scrollToLetter(letter)}
                  className={`brand-alphabet-link ${activeLetter === letter ? 'active' : ''}`}
                  data-testid={`query-alphabet-${letter}`}
                >
                  {letter}
                </button>
              ))}
            </nav>
            
            <div className="brand-query-columns">
              {queryLetters.map(letter => (
                <div key={letter} id={`query-letter-${letter}`} className="brand-query-section">
                  <h3 className="brand-query-section-letter">{letter}</h3>
                  <ul className="brand-query-links">
                    {brandData.queries_by_letter[letter].map(q => (
                      <li key={q.full_query}>
                        <a
                          href={`/?query=${encodeURIComponent(q.full_query)}&page=1`}
                          className="brand-query-link"
                          data-testid={`query-link-${q.full_query.replace(/\s+/g, '-')}`}
                        >
                          {q.full_query}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default BrandPage;
